import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Button, Table, Popconfirm, Input, Divider, message } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { EditOutlined } from '@ant-design/icons';
import { cloneDeep } from 'lodash';
import intl from 'react-intl-universal';
import { useParams } from 'react-router-dom';

import { DATE_TIME_FORMAT, PERM_CODES } from '@/config';
import { getTimeByUtcOffset } from '@/utils/intl';
import * as BillApi from '@/api/bill';
import { updateBillList, setSelectedBills, setSelectedBillIds } from '@/redux/actions/bill';
import useGoodsReversal from '@/pages/goods/reversal';
import { parseFen2Yuan } from '@/utils/number';
import AuthBtn from '@/components/AuthBtn';
import RefundDetailDrawer from '../reports/refundAccounts/components/RefundDetailDrawer';

import { PartRefundDrawerRef, ReverseDrawerRef, CrossRefundDrawerRef } from './interface';
import BillPartRefund from './partRefund';
import BillReverse from './reverse';
import BillCrossRefund from './crossRefund';

import {
  BILL_TYPE_MAP,
  BILL_STATE_MAP,
  MT_ZL,
  XC_ZL,
  GOODS_SUBJECT_CODE_LIST,
  DY_ZL,
} from './config';
import styles from './index.module.scss';

function BIllTable(props) {
  const { billListLoading, billList, selectedBillIds, reloadList, permissions } = props;

  const partRefundRef = useRef<PartRefundDrawerRef>(null);
  const reverseRef = useRef<ReverseDrawerRef>(null);
  const crossRefundRef = useRef<CrossRefundDrawerRef>(null);
  const { resvNo } = useParams<any>();
  const refundDetailDrawerRef = useRef<any>();

  const inputRef = useRef(null);
  const [saveLoading, setLoading] = useState(false);
  const goodsReversalPortal = useGoodsReversal();

  // reset checkbox
  useEffect(() => {
    props.setSelectedBills([]);
    props.setSelectedBillIds([]);
  }, [billList]);

  useEffect(() => {
    return () => {
      // reset editting state of every list item
      const list = billList.map((item) => {
        return {
          ...item,
          editting: false,
        };
      });
      props.updateBillList(list);
    };
  }, []);

  // 部分退款
  const partRefund = (data) => {
    partRefundRef.current!.openDrawer({
      bill: {
        ...data,
        resvNo,
      },
    });
  };

  // 跨夜审退款
  const crossRefund = (data) => {
    crossRefundRef.current!.openDrawer({
      bill: {
        ...data,
        resvNo,
      },
    });
  };

  // 冲调
  const reverse = (data) => {
    reverseRef.current!.openDrawer({
      bill: {
        ...data,
        resvNo,
      },
    });
  };

  // 商品消费明细
  const goodsDetail = (data) => {
    goodsReversalPortal.showDrawer({
      bill: data,
      billNo: data.no,
      resvNo,
      onEnd: (isModify) => {
        if (isModify) {
          reloadList();
        }
      },
    });
  };

  const rowSelection = {
    fixed: true,
    selectedRowKeys: selectedBillIds,
    onChange: (rowKeys, rows) => {
      let tmpRowKeys = cloneDeep(rowKeys);
      let tmpRows = cloneDeep(rows);

      billList.forEach((bill) => {
        // auto select sub bills
        if (tmpRowKeys.includes(bill.no) && bill.subs && bill.subs.length > 0) {
          bill.subs.forEach((sub) => {
            if (!tmpRowKeys.includes(sub.no)) {
              tmpRowKeys = tmpRowKeys.concat(sub.no);
              tmpRows = tmpRows.concat(sub);
            }
          });
        }
        // remove parent bills
        if (
          bill.reverseItemNo &&
          tmpRowKeys.includes(bill.no) &&
          !tmpRowKeys.includes(bill.reverseItemNo)
        ) {
          if (tmpRowKeys.includes(bill.no)) {
            tmpRowKeys.splice(tmpRowKeys.indexOf(bill.no), 1);
            tmpRows = tmpRows.filter((row) => row.no !== bill.no);
          }
        }
      });
      props.setSelectedBillIds(tmpRowKeys);
      props.setSelectedBills(tmpRows);
    },
    getCheckboxProps: (record) => ({
      // 冲调、优惠、退款账目不可选
      disabled: [2, 3, 4].includes(record.postType),
    }),
  };

  const renderPopconfirmTitle = (value, record) => {
    // 美团直连、携程直连、抖音直连账单不可更改备注
    const canModify =
      record.paymentType !== MT_ZL && record.paymentType !== XC_ZL && record.paymentType !== DY_ZL;

    const updatelList = (bill) => {
      const list = billList.map((item) => {
        return item.no === bill.no ? bill : item;
      });
      props.updateBillList(list);
    };

    const cancelEdit = (data) => {
      const bill = {
        ...data,
        editting: false,
      };
      updatelList(bill);
    };

    const edit = (data) => {
      const bill = {
        ...data,
        editting: true,
      };
      updatelList(bill);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    };

    const save = async (data) => {
      setLoading(true);
      try {
        const remark = inputRef.current?.input?.value;
        const res = await BillApi.updateBillRemark({
          billItemNo: data.no, // 子账单号
          remark, // 备注
          resvNo,
        });
        if (res && res.status) {
          const bill = {
            ...data,
            editting: false,
            remark,
          };
          updatelList(bill);
        } else {
          message.error(res.msg || intl.get('bill_alter_remark_fail'));
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };

    return (
      <>
        {value}
        {canModify ? (
          <Popconfirm
            title={
              <div className={styles['edit-wrapper']}>
                <Input
                  allowClear
                  defaultValue={value}
                  ref={inputRef}
                  placeholder={intl.get('bill_bill_remark_word_limit_200')}
                  maxLength={200}
                  onPressEnter={() => save(record)}
                />
                <Button
                  type="link"
                  loading={saveLoading}
                  onClick={() => save(record)}
                  style={{ marginLeft: 12 }}
                >
                  {intl.get('app_search_form_save_btn')}
                </Button>
                <Divider type="vertical" />
                <Button type="link" onClick={() => cancelEdit(record)}>
                  {intl.get('app_confirm_cancel_btn')}
                </Button>
              </div>
            }
            icon={null}
            placement="bottomRight"
            overlayClassName="editPopConfirm"
            visible={record.editting}
          >
            <Button
              style={{ backgroundColor: 'inherit', border: 'none' }}
              icon={<EditOutlined />}
              onClick={(e) => {
                e.preventDefault();
                edit(record);
              }}
            />
          </Popconfirm>
        ) : (
          ''
        )}
      </>
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: intl.get('app_constants_room_no'),
      dataIndex: 'roomNo',
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_entry_bill_type'),
      dataIndex: 'postType',
      render: (type) => (BILL_TYPE_MAP[type] ? BILL_TYPE_MAP[type] : '-'),
    },
    {
      title: intl.get('bill_refound_res_bill_subject'),
      dataIndex: 'subjectCodeName',
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_refound_res_detail'),
      dataIndex: 'detail',
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_refound_res_pay'),
      dataIndex: 'amount',
      render: (val, record) => {
        // 付款和退款，退款为负
        if ([1, 4].includes(record.postType)) {
          return parseFen2Yuan(record.postType === 4 ? -val : val);
        }
        return '-';
      },
    },
    {
      title: intl.get('bill_consume'),
      dataIndex: 'amount',
      render: (val, record) => {
        // 消费、冲调、优惠，冲调和优惠为负
        if ([0, 2, 3].includes(record.postType)) {
          return parseFen2Yuan([2, 3].includes(record.postType) ? -val : val);
        }
        return '-';
      },
    },
    {
      title: intl.get('bill_invoice_modal_time'),
      dataIndex: 'createTime',
      render: (val) => getTimeByUtcOffset(val, DATE_TIME_FORMAT),
    },
    {
      title: intl.get('bill_operator'),
      dataIndex: 'createBy',
      render: (v) => v || '-',
    },
    {
      title: intl.get('bill_check_status'),
      dataIndex: 'status',
      render: (status) => (BILL_STATE_MAP[status] ? BILL_STATE_MAP[status] : '-'),
    },
    {
      title: intl.get('bill_remark'),
      dataIndex: 'remark',
      render: (val, record) => {
        return renderPopconfirmTitle(val, record);
      },
    },
    {
      title: intl.get('app_table_column_action'),
      fixed: 'right',
      align: 'right',
      render: (val, record) => {
        return (
          <>
            <AuthBtn>
              {
                // 未结
                // 入账类型是付款
                record.status === 0 && record.postType === 1 ? (
                  <Button
                    data-authkey={PERM_CODES.PART_REFUND}
                    type="link"
                    className={styles.operateBtn}
                    onClick={() => {
                      partRefund(record);
                    }}
                  >
                    {intl.get('bill_part_refund_title')}
                  </Button>
                ) : null
              }
              {
                // 有权限
                // 已结
                // 入账类型是付款
                record.crossNightFlag && record.status === 1 && record.postType === 1 ? (
                  <Button
                    data-authkey={PERM_CODES.BILL_CROSS_NIGHT_AUDIT_REFUND}
                    type="link"
                    className={styles.operateBtn}
                    onClick={() => {
                      crossRefund(record);
                    }}
                  >
                    {intl.get('bill_cross_refound_title')}
                  </Button>
                ) : null
              }
              {
                // 未结
                // 入账类型是消费
                // 账务项目不是查房商品消费和前台商品消费
                record.status === 0 &&
                record.postType === 0 &&
                !GOODS_SUBJECT_CODE_LIST.includes(record.subjectCode) ? (
                  <Button
                    data-authkey={PERM_CODES.BREW}
                    type="link"
                    className={styles.operateBtn}
                    onClick={() => {
                      reverse(record);
                    }}
                  >
                    {intl.get('bill_config_chong_tiao')}
                  </Button>
                ) : null
              }
              {
                // 入账类型是消费
                // 账务项目是查房商品消费或前台商品消费
                record.postType === 0 && GOODS_SUBJECT_CODE_LIST.includes(record.subjectCode) ? (
                  <Button
                    data-authkey={PERM_CODES.CONSUME_DETAIL}
                    type="link"
                    className={styles.operateBtn}
                    onClick={() => {
                      goodsDetail(record);
                    }}
                  >
                    {intl.get('bill_consume_detail')}
                  </Button>
                ) : null
              }
              {
                // 退款详情
                // 入账类型是退款
                record.postType === 4 && (
                  <Button
                    type="link"
                    className={styles.operateBtn}
                    onClick={() => {
                      refundDetailDrawerRef.current.openDrawer(
                        {
                          ...record,
                          isRoomOrder: true,
                          crossNight: record.crossNightFlag,
                        },
                        props.reloadList,
                      );
                    }}
                  >
                    {intl.get('bill_cross_refound_detail')}
                  </Button>
                )
              }
            </AuthBtn>
          </>
        );
      },
    },
  ];

  return (
    <>
      <RefundDetailDrawer ref={refundDetailDrawerRef} />

      <Table
        className={styles['bill-table']}
        rowKey="no"
        rowClassName={(record) => {
          // 冲调、优惠、退款
          if ([2, 3, 4].includes(record.postType)) return 'red-table-cell';
          return '';
        }}
        loading={billListLoading}
        dataSource={billList}
        columns={columns}
        rowSelection={{
          ...rowSelection,
        }}
        // scroll={{ y: 650 }}
        sticky
        pagination={false}
      />
      <BillPartRefund ref={partRefundRef} partRefundCallback={props.reloadList} />
      <BillReverse ref={reverseRef} reverseCallback={props.reloadList} />
      <BillCrossRefund ref={crossRefundRef} crossRefundCallback={props.reloadList} />
    </>
  );
}

const mapStateToProps = (state) => {
  const { billListLoading, billList, selectedBillIds } = state.bill;
  const { permissions } = state.user;
  return { billListLoading, billList, selectedBillIds, permissions };
};

export default connect(mapStateToProps, {
  updateBillList,
  setSelectedBills,
  setSelectedBillIds,
})(BIllTable);
